import React, { useState, useEffect } from 'react';
import '../../Styles/Mode/ModeSliderSelector.css';
import { ModeOptions } from '../../types/mode';

const options: ModeOptions[] = ['low', 'medium', 'high'];

const valueToNumeric = (value: ModeOptions): number => {
  switch (value) {
    case 'low': return 0;
    case 'medium': return 50;
    case 'high': return 100;
    default: return 50;
  }
};

const numericToValue = (numericValue: number): ModeOptions => {
  if (numericValue < 34) return 'low';
  if (numericValue <= 66) return 'medium';
  return 'high';
};

interface SliderSelectorProps {
  value: ModeOptions;
  onChange: (value: ModeOptions) => void;
  disabled?: boolean;
}

export const ModeSliderSelector: React.FC<SliderSelectorProps> = ({ value, onChange, disabled = false }) => {
  const [internalValue, setInternalValue] = useState<number>(() => valueToNumeric(value));

  useEffect(() => {
    setInternalValue(valueToNumeric(value));
  }, [value]);

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setInternalValue(Number(event.target.value));
  };

  const handleDragEnd = () => {
    if (disabled) return;
    const determinedValue = numericToValue(internalValue);
    if (determinedValue !== value) {
      onChange(determinedValue);
    } else {
      setInternalValue(valueToNumeric(determinedValue));
    }
  };

  const handleOptionClick = (optionValue: ModeOptions) => {
    if (disabled) return;
    onChange(optionValue);
  };

  return (
    <div className={`slider-selector ${disabled ? 'disabled' : ''}`}>
      <div className="slider-track-container">
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={internalValue}
          onChange={handleSliderChange}
          onMouseUp={handleDragEnd}
          onTouchEnd={handleDragEnd}
          className="slider-selector-input"
          disabled={disabled}
          aria-label={`${value} slider, current value ${value}`}
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={internalValue}
          aria-valuetext={value}
        />
      </div>
      <div className="slider-selector-options">
        {options.map((option) => (
          <span
            key={option}
            className={`slider-selector-option${option === value ? ' selected' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => handleOptionClick(option)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOptionClick(option);
              }
            }}
            aria-pressed={option === value}
            aria-disabled={disabled}
          >
            {option.charAt(0).toUpperCase() + option.slice(1)}
          </span>
        ))}
      </div>
    </div>
  );
};